import { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Search } from 'lucide-react';
import HeroPersonPills from '../../components/person/HeroPersonPills';
import PersonAddContextControl from '../../components/person/PersonAddContextControl';
import HeroCorporatePills from '../../components/corporate/HeroCorporatePills';
import CorporateAddContextControl from '../../components/corporate/CorporateAddContextControl';
import {
  QUERY_GUIDANCE_MESSAGE,
  validateInvestigationQuery,
} from '../../utils/queryValidator';
import {
  detectInput,
  findDomainInQuery,
  TAB_COMPANY,
  TAB_EMAIL,
  TAB_PERSON,
  TAB_PHONE,
  CORPORATE_DOMAIN_DNS_HINT,
  CORPORATE_DOMAIN_REQUIRED_HINT,
  SINGLE_TERM_INSUFFICIENT_HINT,
  companyFieldHintForQuery,
  isInsufficientSingleTermQuery,
} from './homeSearchUtils';
import { placeholderForModule } from './homeModuleConfig';
import {
  formatPhoneInputLive,
  looksLikePhoneInput,
  normalizePhoneQueryForSubmit,
} from './PhoneLookupHero';

/** Compact GO — ≥36×36 square on phone; expands with label from sm. */
const GO_BTN =
  'inline-flex h-9 w-9 min-h-[36px] min-w-[36px] shrink-0 items-center justify-center rounded-lg p-0 text-xs font-bold leading-none tracking-wide transition-all duration-300 ease-in-out sm:h-10 sm:w-auto sm:min-h-0 sm:min-w-0 sm:rounded-xl sm:px-5 sm:text-sm';

/** Square GO when pills share the row — icon-only until `lg`. */
const GO_BTN_WITH_PILLS =
  'inline-flex h-9 w-9 min-h-[36px] min-w-[36px] shrink-0 items-center justify-center rounded-lg p-0 text-xs font-bold leading-none tracking-wide transition-all duration-300 ease-in-out lg:h-9 lg:w-auto lg:min-h-0 lg:min-w-0 lg:rounded-xl lg:px-4 lg:text-sm';

const ACTION_BTN =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-600/70 bg-transparent text-slate-300 transition-all duration-300 ease-in-out hover:border-red-500/30 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-35 sm:h-10 sm:w-10 sm:rounded-xl';

const INLINE_CLEAR_BTN =
  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm font-bold text-slate-400 transition hover:bg-white/10 hover:text-slate-100 sm:h-8 sm:w-8';

export default function HomeHeroSearch({
  query,
  onQueryChange,
  activeModule,
  loading = false,
  canReport = false,
  onSubmitSearch,
  onCancel,
  onOpenReport,
  canReset = false,
  onReset,
  companyDomainCheck,
  emailDomainCheck,
  heroPills = null,
  heroPillKind = null,
  pillDraft = '',
  onPillDraftChange,
  onMaterializePills,
  onCyclePillType,
  onSetPillType,
  onRemovePill,
  /** After scan/results: tighter mobile chrome (Maps/Twitter-style compact app bar). */
  compactChrome = false,
}) {
  const inputRef = useRef(null);
  const pendingPersonPillTypeRef = useRef(null);
  const pendingCorporatePillTypeRef = useRef(null);
  const [compactPlaceholder, setCompactPlaceholder] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia('(max-width: 639px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(max-width: 639px)');
    const sync = () => setCompactPlaceholder(mq.matches);
    sync();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  const hasPills = Array.isArray(heroPills) && heroPills.length > 0;
  const showPersonPills = hasPills && heroPillKind === 'person' && activeModule === TAB_PERSON;
  const showCorporatePills =
    hasPills && heroPillKind === 'company' && activeModule === TAB_COMPANY;
  const showPills = showPersonPills || showCorporatePills;
  /** Scan / post-scan: single-line centered pills; hide draft and + to avoid dead space. */
  const pillsLocked = showPills && (loading || compactChrome);
  const inputValue = showPills ? pillDraft : query;

  const focusPersonDraftInput = useCallback(() => {
    const el = inputRef.current;
    if (el && typeof el.focus === 'function') el.focus();
  }, []);

  const composedForValidation = showPills
    ? [query, String(pillDraft || '').trim()].filter(Boolean).join(' ').trim() || query
    : String(query || '').trim();
  const queryValidation = validateInvestigationQuery(
    composedForValidation,
    activeModule || detectInput(composedForValidation)?.tab || '',
  );
  const queryFormatBlocked =
    Boolean(composedForValidation) &&
    !queryValidation.isValid &&
    queryValidation.reason !== 'EMPTY';

  const resolveSubmit = useCallback(
    (rawQuery) => {
      const q = String(rawQuery ?? '').trim();
      if (!q && !hasPills) return null;
      const tab = activeModule ?? detectInput(q)?.tab;
      if (!tab) return null;
      const companyField = tab === TAB_COMPANY ? companyFieldHintForQuery(q) : undefined;
      return { query: q, tab, companyField };
    },
    [activeModule, hasPills],
  );

  const runSearch = useCallback(
    (rawQuery) => {
      const payload = resolveSubmit(rawQuery);
      if (
        !payload &&
        !(
          (activeModule === TAB_PERSON || activeModule === TAB_COMPANY) &&
          hasPills
        )
      ) {
        return;
      }
      const qCheck = String(
        payload?.query ||
          (hasPills
            ? [query, String(rawQuery || '').trim()].filter(Boolean).join(' ').trim()
            : rawQuery) ||
          '',
      ).trim();
      const tabCheck = payload?.tab || activeModule || '';
      const v = validateInvestigationQuery(qCheck, tabCheck);
      if (!v.isValid) return;
      const pillHasDomainNow =
        hasPills &&
        heroPillKind === 'company' &&
        Array.isArray(heroPills) &&
        heroPills.some((p) => p.type === 'domain' && String(p.text || '').trim());
      if (
        tabCheck === TAB_COMPANY &&
        !findDomainInQuery(qCheck) &&
        !pillHasDomainNow
      ) {
        return;
      }
      if (
        (tabCheck === TAB_PERSON || tabCheck === TAB_COMPANY) &&
        isInsufficientSingleTermQuery(qCheck) &&
        !(tabCheck === TAB_COMPANY && pillHasDomainNow)
      ) {
        return;
      }
      onSubmitSearch(
        payload || {
          query: String(rawQuery || '').trim(),
          tab: activeModule,
        },
      );
    },
    [activeModule, hasPills, heroPillKind, heroPills, onSubmitSearch, query, resolveSubmit],
  );

  const resolvedDetection = query.trim() ? detectInput(query) : null;
  const emailDnsBlocksGo =
    activeModule === TAB_EMAIL && emailDomainCheck?.nsResolved === false;
  const companyDnsBlocksGo =
    activeModule === TAB_COMPANY &&
    companyDomainCheck?.syntaxValid === true &&
    companyDomainCheck?.nsResolved === false;
  const corpPillHasDomain =
    showCorporatePills &&
    Array.isArray(heroPills) &&
    heroPills.some((p) => p.type === 'domain' && String(p.text || '').trim());
  const companyNeedsDomainBlocksGo =
    activeModule === TAB_COMPANY &&
    !findDomainInQuery(composedForValidation) &&
    !corpPillHasDomain;
  const singleTermBlocksGo =
    (activeModule === TAB_PERSON || activeModule === TAB_COMPANY) &&
    isInsufficientSingleTermQuery(composedForValidation) &&
    !(activeModule === TAB_COMPANY && corpPillHasDomain);
  const canGo =
    (Boolean(query.trim()) || hasPills) &&
    Boolean(activeModule ?? resolvedDetection?.tab) &&
    !emailDnsBlocksGo &&
    !companyDnsBlocksGo &&
    !companyNeedsDomainBlocksGo &&
    !singleTermBlocksGo &&
    !queryFormatBlocked;
  const submitBlocked = !loading && !canGo;

  const onFormSubmit = (e) => {
    e.preventDefault();
    if (loading) {
      onCancel?.();
      return;
    }
    if (
      emailDnsBlocksGo ||
      companyDnsBlocksGo ||
      companyNeedsDomainBlocksGo ||
      singleTermBlocksGo ||
      queryFormatBlocked
    ) {
      return;
    }
    const field = e.currentTarget.elements.namedItem('hero-query');
    const live = field && 'value' in field ? field.value : inputValue;
    const submitQuery = showPills
      ? [query, String(live || '').trim()].filter(Boolean).join(' ').trim() || query
      : live;
    const tab = activeModule ?? detectInput(String(submitQuery || '').trim())?.tab;
    const normalized =
      tab === TAB_PHONE || looksLikePhoneInput(submitQuery)
        ? normalizePhoneQueryForSubmit(submitQuery)
        : submitQuery;
    runSearch(normalized);
  };

  const handleInputChange = (e) => {
    const next = e.target.value;
    if (showPills) {
      onPillDraftChange?.(next);
      return;
    }
    if (activeModule === TAB_PHONE || looksLikePhoneInput(next)) {
      if (looksLikePhoneInput(next)) {
        const { formatted } = formatPhoneInputLive(next);
        onQueryChange(formatted);
        return;
      }
    }
    onQueryChange(next);
  };

  const handleInputBlur = () => {
    if (activeModule !== TAB_PERSON && activeModule !== TAB_COMPANY) return;
    window.setTimeout(() => {
      onMaterializePills?.({ force: false, module: activeModule });
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (
      e.key === 'Enter' &&
      (activeModule === TAB_PERSON || activeModule === TAB_COMPANY)
    ) {
      e.preventDefault();
      onMaterializePills?.({
        force: true,
        draft: showPills ? pillDraft : undefined,
        module: activeModule,
      });
      // Explicit submit — do not rely on implicit form Enter (unreliable with
      // pill draft input / disabled submit styling edge cases).
      const field = e.currentTarget;
      const live = field && 'value' in field ? field.value : inputValue;
      const submitQuery = showPills
        ? [query, String(live || '').trim()].filter(Boolean).join(' ').trim() || query
        : live;
      const normalized =
        activeModule === TAB_PHONE || looksLikePhoneInput(submitQuery)
          ? normalizePhoneQueryForSubmit(submitQuery)
          : submitQuery;
      if (!loading) {
        if (companyNeedsDomainBlocksGo || singleTermBlocksGo) return;
        runSearch(normalized);
      } else {
        onCancel?.();
      }
      return;
    }
    if (e.key !== 'Backspace') return;
    if (!showPills) return;
    if (String(pillDraft || '').length > 0) return;
    if (!heroPills?.length) return;
    e.preventDefault();
    onRemovePill?.(heroPills[heroPills.length - 1].id);
  };

  const showDomainEnableHint =
    companyNeedsDomainBlocksGo && companyDomainCheck?.nsResolved !== false;

  const showSingleTermHint =
    singleTermBlocksGo && !showDomainEnableHint;

  const showCompanyDnsHint =
    activeModule === TAB_COMPANY &&
    companyDomainCheck?.syntaxValid === true &&
    companyDomainCheck?.nsResolved === false &&
    Boolean(companyDomainCheck?.message);

  const showEmailDnsHint =
    activeModule === TAB_EMAIL &&
    emailDomainCheck?.nsResolved === false &&
    Boolean(emailDomainCheck?.message);

  const showQueryFormatHint =
    queryFormatBlocked &&
    !showCompanyDnsHint &&
    !showEmailDnsHint &&
    !showDomainEnableHint &&
    !showSingleTermHint;

  const phoneLive =
    !showPills &&
    (activeModule === TAB_PHONE || looksLikePhoneInput(inputValue)) &&
    looksLikePhoneInput(inputValue)
      ? formatPhoneInputLive(inputValue)
      : null;
  const phoneFlag = phoneLive?.flag || '';

  const showInlineClear =
    !loading && (Boolean(String(inputValue || '').trim()) || Boolean(canReset));

  const handleInlineClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (canReset && typeof onReset === 'function') {
      onReset();
      return;
    }
    if (showPills && typeof onPillDraftChange === 'function') {
      onPillDraftChange('');
      return;
    }
    onQueryChange?.('');
  };

  const goTone = loading
    ? 'bg-gradient-to-r from-amber-600 to-red-600 text-white shadow-md shadow-red-500/25 hover:brightness-110'
    : `bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/25 hover:bg-cyan-400 ${
        submitBlocked ? 'cursor-not-allowed opacity-40' : ''
      }`;

  /**
   * Plain input GO expands from 400px; pill-row GO stays square until `sm`
   * so CANCEL never overflows the amber disc.
   */
  const goLabelPlain = (
    <span
      key={loading ? 'cancel' : 'go'}
      className="inline-flex items-center justify-center leading-none animate-in fade-in duration-300"
    >
      {loading ? (
        <>
          <span className="min-[400px]:hidden" aria-hidden>
            ✕
          </span>
          <span className="hidden min-[400px]:inline">CANCEL</span>
        </>
      ) : (
        <>
          <Search className="h-4 w-4 min-[400px]:hidden" aria-hidden strokeWidth={2.5} />
          <span className="hidden min-[400px]:inline">GO!</span>
        </>
      )}
    </span>
  );

  const goLabelPills = (
    <span
      key={loading ? 'cancel-pills' : 'go-pills'}
      className="inline-flex items-center justify-center leading-none animate-in fade-in duration-300"
    >
      {loading ? (
        <>
          <span className="lg:hidden" aria-hidden>
            ✕
          </span>
          <span className="hidden lg:inline">CANCEL</span>
        </>
      ) : (
        <>
          <Search className="h-4 w-4 lg:hidden" aria-hidden strokeWidth={2.5} />
          <span className="hidden lg:inline">GO!</span>
        </>
      )}
    </span>
  );

  return (
    <>
      <section className="w-full min-w-0">
        <section className="home-search-hero-glow w-full min-w-0">
          <form
            className={`home-glass-card flex w-full min-w-0 items-center gap-1 rounded-2xl border border-slate-700 bg-slate-900/80 p-1 shadow-2xl shadow-black/40 transition-all duration-200 sm:gap-1.5 sm:p-2 ${
              showPills ? 'justify-between' : ''
            }`}
            onSubmit={onFormSubmit}
            data-testid="home-hero-search-form"
            data-compact-chrome={compactChrome ? 'true' : undefined}
            data-pills-locked={pillsLocked ? 'true' : undefined}
          >
            {/*
              Full-width search row. Pills cluster left; GO/trash stay adjacent (no
              side-by-side module grid — that crushed mobile input).
            */}
            <label
              className={`relative flex min-w-0 flex-1 items-center gap-1 rounded-xl border border-slate-700 bg-slate-950 pl-1.5 pr-1 transition-all duration-200 focus-within:border-cyan-500/50 focus-within:ring-1 focus-within:ring-cyan-500/40 sm:gap-1.5 sm:pl-2.5 sm:pr-1.5 ${
                showPills
                  ? pillsLocked
                    ? 'h-7 min-h-7 overflow-hidden py-0 sm:h-10 sm:min-h-10'
                    : 'h-7 min-h-7 min-w-0 overflow-hidden py-0 sm:h-auto sm:min-h-[2.625rem] sm:overflow-visible sm:py-1.5'
                  : compactChrome
                    ? 'h-8 min-h-8 overflow-hidden sm:h-10 sm:min-h-10'
                    : 'h-9 min-h-9 overflow-hidden sm:h-11 sm:min-h-11'
              }`}
            >
              {phoneFlag ? (
                <span
                  className="mt-0.5 shrink-0 text-lg leading-none"
                  aria-label={phoneLive?.country ? `Country ${phoneLive.country}` : 'Detected country'}
                  data-testid="phone-input-flag"
                >
                  {phoneFlag}
                </span>
              ) : null}

              {showPills ? (
                <div
                  className={`hero-pills-scroll flex min-w-0 flex-1 items-center gap-1 sm:gap-1.5 ${
                    pillsLocked
                      ? 'flex-nowrap overflow-x-auto overscroll-x-contain touch-pan-x [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
                      : 'flex-nowrap overflow-x-auto overscroll-x-contain touch-pan-x [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap sm:content-center sm:overflow-visible sm:overscroll-auto'
                  }`}
                  data-testid="hero-pills-scroll"
                >
                  {showPersonPills ? (
                    <HeroPersonPills
                      pills={heroPills}
                      onCycleType={onCyclePillType}
                      onSetType={onSetPillType}
                      onRemove={pillsLocked ? undefined : onRemovePill}
                    />
                  ) : null}
                  {showCorporatePills ? (
                    <HeroCorporatePills
                      pills={heroPills}
                      onCycleType={onCyclePillType}
                      onSetType={onSetPillType}
                      onRemove={pillsLocked ? undefined : onRemovePill}
                    />
                  ) : null}
                  {showPills && !pillsLocked ? (
                    <input
                      ref={inputRef}
                      type="text"
                      name="hero-query"
                      enterKeyHint="search"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      value={inputValue}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      onKeyDown={handleKeyDown}
                      onFocus={(e) => {
                        // Prevent browser from sideways-scrolling ancestors to the caret.
                        const scroller = e.currentTarget.closest('[data-testid="hero-pills-scroll"]');
                        if (scroller) scroller.scrollLeft = 0;
                      }}
                      placeholder={
                        showPersonPills || showCorporatePills ? '' : 'Add…'
                      }
                      size={Math.max(2, Math.min(12, (inputValue || '').length + 1))}
                      className="h-7 min-w-[2.5rem] max-w-[8rem] flex-none bg-transparent py-0.5 text-xs font-medium text-white placeholder:text-slate-300 focus:outline-none sm:h-8 sm:text-sm"
                      aria-label="Primary intelligence search"
                      aria-invalid={queryFormatBlocked || undefined}
                    />
                  ) : null}
                  {showPersonPills && !pillsLocked ? (
                    <PersonAddContextControl
                      compact
                      onFocusInput={focusPersonDraftInput}
                      onPickType={(type) => {
                        pendingPersonPillTypeRef.current = type;
                        const placeholders = {
                          name: 'Add a name…',
                          location: 'Add a location…',
                          focus: 'Add context…',
                          username: 'Add @username…',
                        };
                        if (inputRef.current) {
                          inputRef.current.placeholder = placeholders[type] || '';
                        }
                        focusPersonDraftInput();
                      }}
                    />
                  ) : null}
                  {showCorporatePills && !pillsLocked ? (
                    <CorporateAddContextControl
                      compact
                      onFocusInput={focusPersonDraftInput}
                      onPickType={(type) => {
                        pendingCorporatePillTypeRef.current = type;
                        const placeholders = {
                          domain: 'Add a domain…',
                          company: 'Add a company…',
                          location: 'Add a location…',
                          person: 'Add a person…',
                        };
                        if (inputRef.current) {
                          inputRef.current.placeholder = placeholders[type] || '';
                        }
                        focusPersonDraftInput();
                      }}
                    />
                  ) : null}
                </div>
              ) : null}

              {pillsLocked ? (
                <input
                  ref={inputRef}
                  type="text"
                  name="hero-query"
                  value={inputValue}
                  readOnly
                  tabIndex={-1}
                  aria-hidden
                  className="sr-only"
                />
              ) : null}

              {!showPills ? (
                <>
                  <input
                    ref={inputRef}
                    type="text"
                    name="hero-query"
                    enterKeyHint="search"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholderForModule(activeModule, {
                      compact: compactPlaceholder,
                    })}
                    className={`h-full min-w-0 w-full flex-1 overflow-x-auto bg-transparent py-1.5 pl-0.5 text-base font-sans font-medium text-white placeholder:text-slate-300 focus:outline-none sm:pl-1 sm:text-base md:text-lg ${
                      showInlineClear ? 'pr-[5.25rem] sm:pr-[6.75rem]' : 'pr-11 sm:pr-24'
                    }`}
                    aria-label="Primary intelligence search"
                    aria-invalid={queryFormatBlocked || undefined}
                  />
                  {showInlineClear ? (
                    <button
                      type="button"
                      onClick={handleInlineClear}
                      className={`absolute right-[2.85rem] z-10 sm:right-[5.5rem] ${INLINE_CLEAR_BTN}`}
                      aria-label="Clear search"
                      title="Clear"
                      data-testid="hero-inline-clear"
                    >
                      ✕
                    </button>
                  ) : null}
                  <button
                    type="submit"
                    disabled={submitBlocked}
                    aria-disabled={submitBlocked}
                    aria-label={loading ? 'Cancel scan' : 'Start search'}
                    data-testid="hero-go-cancel"
                    className={`absolute right-0.5 z-10 ${GO_BTN} ${goTone}`}
                  >
                    {goLabelPlain}
                  </button>
                </>
              ) : null}
            </label>

            {showPills ? (
              <button
                type="submit"
                disabled={submitBlocked}
                aria-disabled={submitBlocked}
                aria-label={loading ? 'Cancel scan' : 'Start search'}
                data-testid="hero-go-cancel"
                className={`${GO_BTN_WITH_PILLS} ${goTone} self-center`}
              >
                {goLabelPills}
              </button>
            ) : null}

            {showPills && showInlineClear ? (
              <button
                type="button"
                onClick={handleInlineClear}
                className={`${INLINE_CLEAR_BTN} self-center`}
                aria-label="Clear search"
                title="Clear"
                data-testid="hero-inline-clear-pills"
              >
                ✕
              </button>
            ) : null}

            {canReport ? (
              <button
                type="button"
                onClick={() => onOpenReport?.()}
                aria-label="Open report"
                data-testid="hero-report"
                className={`${ACTION_BTN} self-center min-w-8 sm:min-w-fit sm:gap-1.5 sm:px-2.5`}
              >
                <FileText className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                <span className="hidden sm:inline text-xs font-bold uppercase tracking-wider text-slate-200">
                  Report
                </span>
              </button>
            ) : null}
          </form>
        </section>
      </section>

      {showQueryFormatHint ? (
        <p
          className="mx-auto mt-2 max-w-3xl animate-in fade-in slide-in-from-top-1 rounded-lg border border-slate-500/30 bg-slate-900 px-3 py-2 text-center text-xs font-medium leading-relaxed text-slate-200 duration-300 sm:text-sm"
          role="status"
          aria-live="polite"
        >
          {queryValidation.message || QUERY_GUIDANCE_MESSAGE}
        </p>
      ) : null}

      {showDomainEnableHint ? (
        <p
          className="mx-auto mt-2 max-w-3xl rounded-lg border border-amber-500/35 bg-amber-950/50 px-3 py-2 text-center text-xs font-medium leading-relaxed text-amber-50 sm:text-sm"
          role="status"
          aria-live="polite"
        >
          {CORPORATE_DOMAIN_REQUIRED_HINT}
        </p>
      ) : null}

      {showSingleTermHint ? (
        <p
          className="mx-auto mt-2 max-w-3xl rounded-lg border border-amber-500/35 bg-amber-950/50 px-3 py-2 text-center text-xs font-medium leading-relaxed text-amber-50 sm:text-sm"
          role="status"
          aria-live="polite"
        >
          {SINGLE_TERM_INSUFFICIENT_HINT}
        </p>
      ) : null}

      {showCompanyDnsHint ? (
        <p
          className="mx-auto mt-2 max-w-3xl animate-in fade-in slide-in-from-top-1 rounded-lg border border-amber-500/35 bg-amber-950/50 px-3 py-2 text-center text-xs font-medium leading-relaxed text-amber-50 duration-300 sm:text-sm"
          role="status"
          aria-live="polite"
        >
          {companyDomainCheck.message || CORPORATE_DOMAIN_DNS_HINT}
        </p>
      ) : null}

      {showEmailDnsHint ? (
        <p
          className="mx-auto mt-2 max-w-3xl animate-in fade-in slide-in-from-top-1 rounded-lg border border-amber-500/35 bg-amber-950/50 px-3 py-2 text-center text-xs font-medium leading-relaxed text-amber-50 duration-300 sm:text-sm"
          role="status"
          aria-live="polite"
        >
          {emailDomainCheck.message}
        </p>
      ) : null}

      {activeModule === TAB_COMPANY && companyDomainCheck?.checking ? (
        <p className="mx-auto mt-1 max-w-3xl text-center text-xs font-medium text-slate-300" role="status">
          Checking domain…
        </p>
      ) : null}

      {activeModule === TAB_EMAIL && emailDomainCheck?.checking ? (
        <p className="mx-auto mt-1 max-w-3xl text-center text-xs font-medium text-slate-300" role="status">
          Checking DNS…
        </p>
      ) : null}
    </>
  );
}
